import React, { useState } from "react";
import { motion } from "framer-motion";
import { fadeIn, staggerContainer } from "../variants";
import { donateData } from "../data";
import { Heart, Copy, Check, ExternalLink } from "lucide-react";
import DonateImg from "../img/donate/donate0.jpg";

const Donate = () => {
  const { eyebrow, title, subtitle, bankTransfer, onlineLink } = donateData;
  const [copied, setCopied] = useState(false);

  const copyIBAN = () => {
    navigator.clipboard.writeText(bankTransfer.iban.replace(/\s/g, ""));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="donate" className="section scroll-mt-20 relative overflow-hidden">

      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-transparent to-primary/5 pointer-events-none" />

      <div className="container mx-auto relative">

        {/* Section header */}
        <motion.div
          variants={staggerContainer(0.12, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="section-header"
        >
          <motion.p variants={fadeIn("up")} className="eyebrow mb-4">
            {eyebrow}
          </motion.p>
          <motion.h2 variants={fadeIn("up", 0.05)} className="h2 mb-6">
            {title}
          </motion.h2>
          <motion.p variants={fadeIn("up", 0.1)} className="lead">
            {subtitle}
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">

          {/* Left — Image + quote */}
          <motion.div
            variants={fadeIn("right")}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-card-hover">
              <img
                src={DonateImg}
                alt="Enfants de l'école APPE"
                className="w-full h-80 lg:h-[480px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/70 via-dark/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-5 border border-white/20">
                  <Heart size={20} fill="currentColor" className="text-secondary mb-3" />
                  <p className="text-white font-heading text-lg leading-tight">
                    "Chaque don, quelle que soit sa taille, change concrètement la vie d'un enfant."
                  </p>
                  <p className="text-white/60 text-sm mt-2">— L'équipe APPE Tchad</p>
                </div>
              </div>
            </div>

            {/* Tax deductible badge */}
            <div className="absolute -top-4 -right-4 bg-accent text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
              66% déductible des impôts
            </div>
          </motion.div>

          {/* Right — Donation options */}
          <motion.div
            variants={fadeIn("left")}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="space-y-6"
          >
            {/* Online donation CTA */}
            <a
              href={onlineLink.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-3 bg-secondary hover:bg-secondary-dark text-white font-bold text-base py-4 px-8 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 group"
            >
              <Heart size={18} fill="currentColor" />
              {onlineLink.text}
              <ExternalLink size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </a>

            {/* Bank transfer */}
            <div className="bg-white rounded-2xl p-6 shadow-card border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-primary rounded-full" />
                <h4 className="font-semibold text-dark">{bankTransfer.title}</h4>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-sm text-grey">{bankTransfer.name}</p>
                <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                  <div>
                    <div className="text-xs text-grey mb-0.5">IBAN</div>
                    <div className="font-mono text-sm font-medium text-dark">{bankTransfer.iban}</div>
                  </div>
                  <button
                    onClick={copyIBAN}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark transition-colors font-medium ml-4"
                  >
                    {copied ? <Check size={14} className="text-accent" /> : <Copy size={14} />}
                    {copied ? "Copié !" : "Copier"}
                  </button>
                </div>
                <div className="flex gap-4">
                  <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-100">
                    <div className="text-xs text-grey mb-0.5">BIC</div>
                    <div className="font-mono text-sm font-medium text-dark">{bankTransfer.bic}</div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-grey italic">{bankTransfer.note}</p>
            </div>

            {/* Trust signals */}
            <div className="flex flex-wrap gap-3">
              {[
                "Association loi 1901",
                "Intérêt général reconnu",
                "Reçus fiscaux délivrés",
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs text-grey bg-light px-3 py-1.5 rounded-full border border-gray-100">
                  <Check size={12} className="text-accent" />
                  {t}
                </div>
              ))}
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default Donate;
